/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import moment from "moment";
import _ from "lodash";
import config from "../config/assetDetail.config.json";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_ASSET_DETAIL } from "../graphql";
import { VApplication } from "../../../application";
import { AppNavigatorService } from "../../appNavigatorService";
import { DEFAULT_APP_CONFIG } from "../../../config/default.config";
import { useAssetId } from "../../../pages/AssetDetail/provider";
import { useAsset } from "../provider";
import { toHeaderCase } from "js-convert-case";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IAssetDetailMethods extends IPluginMethodMap {
  getAssetDetailProps(args: any): Promise<any>;
}

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

const DEFAULT_VIDEO_ICON = "https://i.ibb.co/N3R8JRk/video-camera-png.webp";
const DEFAULT_AUDIO_POSTER = "https://i.ibb.co/9gvQF5r/audio-poster.jpg";
const DEFAULT_PDF_POSTER =
  "https://s3.ap-south-1.amazonaws.com/io.vlinder.illustrations/pdf-illustration.png";

export class AssetDetailService implements IAgentPlugin {
  readonly methods: IAssetDetailMethods;

  constructor() {
    this.methods = {
      getAssetDetailProps: this.getAssetDetailProps.bind(this),
    };
  }

  async getAssetDetailProps(args: any) {
    const _res: any = [];
    const { assetId } = useAsset();
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.query({
          query: GET_ASSET_DETAIL,
          variables: {
            verifyCredentialPublicId: assetId,
            // storeId: DEFAULT_APP_CONFIG.storeId,
          },
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          console.log("asset =>", res?.data?.verifyCredentialPublic);
          let _res = res?.data?.verifyCredentialPublic;
          return {
            data: res?.data?.verifyCredentialPublic,
            isWalletRegistered:this.getWalletRegistered(_res),
            // imageItems: this.formatImages(_res?.image),
            logoSrc: _res?.issuerLogo,
            pdfFile: _res?.pdf,
            title: this.formatTitle(_res),
            badgeTitle: _res?.status,
            badgeTitleBg: this.formatBadgeTitleBg(_res),
            subtitle: this.formatSubTitle(_res),
            description: _res?.description,
            credential: this.formatAttributes(_res),
            attributeItems: this.formatAttributeItems(
              _res
            ),
            tableItems: this.formatDigitalSignatures(
              _res
            ),
            carouselItems: this.formatCarouselItems(
              _res
            ),
            listItemTitle: this.formatGetListItemTitle(),
          };
        }
      }
    } catch (err: any) {
      console.log("error in get Asset Detail =>", err);
      throw err;
    }
    // return config;
  }


  private getWalletRegistered(credential:any){
    let isWalletRegistered = false;

    if(credential?.accountStatus === "WALLET_REGISTERED"){
      isWalletRegistered = true;
    }
    
    return isWalletRegistered;
  }

  private formatAttributes(credential?: any) {
    let _attributeData: any = [];
    let formatted: any = { ...(credential?.credentialSubject || {}) };
    if (formatted) {
      for (let key in formatted) {
        if (config?.credentialSubject?.blacklistedKeys?.includes(key)) {
          delete formatted[key];
        }
      }
    }

    Object.entries(formatted).forEach(([key, value]) => {
      if(!Array.isArray(value)){
        _attributeData.push({
          label: toHeaderCase(key ?? ""),
          value: value,
        });
      }
    });
    return _attributeData;
  }

  private formatBadgeTitleBg(credential?: any) {
    let formatted: any = "danger";
    if (credential?.status?.toLowerCase()?.includes("active")) {
      formatted = "success";
    }
    return formatted;
  }

  private formatTitle(credential: any) {
    let formatted = credential?.credentialName ?? "";
    return toHeaderCase(formatted);
  }

  private formatSubTitle(credential: any) {
    let formatted = credential?.issuer ?? "";
    return toHeaderCase(formatted);
  }

  private formatImages(image?: string) {
    const formatted: any = [];
    if (image) {
      formatted.push({
        original: `data:image/png;base64,${image}`,
        thumbnail: `data:image/png;base64,${image}`,
      });
    }
    return formatted;
  }

  private formatGetListItemTitle() {
    return config?.digitalSignatures?.title;
  }

  private formatAttributeItems(credential: any) {
    const formatted: any = {
      title: config?.digitalSignatures?.title,
      info: config?.attributeItems,
    };
    if (credential) {
      for (let credKey in credential) {
        if (credKey === "issuer") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "digitalSignedBy"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "issuerDid") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "signerDID"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "issuerPk") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "signerPublicKey"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "validFrom") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "validFrom"
          );
          if (index > -1) {
            formatted.info[index].value = this.formatFromDate(credential[credKey] ?? null);
          }
        } else if (credKey === "validUntil") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "validUntil"
          );
          if (index > -1) {
            formatted.info[index].value = this.formatDate(credential[credKey] ?? null);
          }
        } else if (credKey === "status") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "status"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else {
        }
      }
    }
    return formatted?.info;
  }

  private formatDigitalSignatures(credential: any) {
    const formatted: any = {
      title: config?.digitalSignatures?.title,
      info: config?.digitalSignatures?.info,
    };
    if (credential) {
      for (let credKey in credential) {
        if (credKey === "issuer") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "digitalSignedBy"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "issuerDid") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "signerDID"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "issuerPk") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "signerPublicKey"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "validFrom") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "validFrom"
          );
          if (index > -1) {
            formatted.info[index].value = this.formatFromDate(credential[credKey]?? null);
          }
        } else if (credKey === "validUntil") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "validUntil"
          );
          console.log(`find index`, index);
          if (index > -1) {
            formatted.info[index].value = this.formatDate(credential[credKey] ?? null);
          }
        } else if (credKey === "status") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "status"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else {
        }
      }
    }
    return formatted?.info;
  }

  private formatDate(date: string) {
    if (date) {
      return moment(date).format("lll");
    } else {
      return "Perpetual";
    }
  }

  private formatFromDate(date:string){
    if(date){
      return moment(date).format("lll");
    } else {
      return "-"
    }
  }

  private formatCarouselItems(credential: any) {
    let _docs: any = [];
    if (credential?.pdf) {
      const src = isValidHttpUrl(credential?.pdf)
        ? credential?.pdf
        : `data:application/pdf;base64,${credential?.pdf}`;
      _docs.push({
        type: "pdf",
        poster: credential?.logo,
        src: src,
        title: credential?.credentialName,
      });
    }
    Array.isArray(credential?.evidence) &&
      credential?.evidence.map((item: any) => {
        let _type =item?.evidenceDocumentType?.split("/");
        _docs.push({
          type: _type[0] ==="image" ? "img": _type[0] ==="video" ? "video" : _type[0] ==="audio" ? "audio": "pdf", 
          poster: item?.poster,
          src: item?.id,
          title: item?.evidenceDocument?.replace(/\.[^/.]+$/, ""),
        });
      });
    return _docs;
  }

  
}
